import classNames from "classnames"
import type {
  ChangeEventHandler,
  ComponentPropsWithoutRef,
  Ref,
} from "react"
import {
  forwardRef,
  useState,
  useCallback,
} from "react"

import styles from "./TextArea.module.scss"
import { UnstyledTextArea } from "src/inputs/UnstyledInputs"

export type Props = ComponentPropsWithoutRef<"textarea">

/*
 * this TextArea component automatically grows when its content grows
 * technique for this component taken from:
 *  https://css-tricks.com/the-cleanest-trick-for-autogrowing-textareas/
 *
 * to use this as an uncontrolled component provide a "defaultValue" property
 * to use this as a controlled component "value" and "onChange" properties
 *
 * providing a "rows" property value will set the minimum number of rows
 */
export const TextArea = forwardRef<HTMLTextAreaElement, Props>(
  function TextArea(props: Props, propRef: Ref<HTMLTextAreaElement>) {
    const {
      value: controlledValue,
      onChange: controlledOnChange,
      defaultValue,
      ...htmlTextAreaProps
    } = props

    const [
      uncontrolledValue,
      setUncontrolledValue,
    ] = useState(defaultValue)

    const onChange = useCallback<ChangeEventHandler<HTMLTextAreaElement>>(
      (e) => {
        setUncontrolledValue(e.target.value)
        controlledOnChange?.(e)
      },
      [
        setUncontrolledValue,
        controlledOnChange,
      ],
    )

    const value = controlledValue ?? uncontrolledValue

    /*
     * the textarea will scale to fill the size of its container
     */
    const className = classNames(
      props.className,
      styles.textArea,
    )

    /*
     * the replica is hidden but forces the container to change size
     * when the content changes
     */
    const replicaClassName = classNames(
      props.className,
      styles.textAreaReplica,
    )

    return (
      <>
        <UnstyledTextArea
          rows={1}
          {...htmlTextAreaProps}
          className={className}
          onChange={onChange}
          value={value}
          ref={propRef}
        />
        <div
          style={htmlTextAreaProps.style}
          className={replicaClassName}
        >
          {`${value} `}
        </div>
      </>
    )
  },
)
